import { EstateType } from "@1games-ru/region-shared/dist/enums/estate/estate";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import formatPrice from "@/core/helpers/formatPrice";
import useAppSelector from "@/core/helpers/useAppSelector";
import { House3Icon } from "@/shared/icons";
import { Button, Checkbox, Modal } from "@/shared/ui";
import Loader from "@/shared/ui/Loader";
import OfferPlayerInfo from "@/shared/widgets/OfferPlayerInfo";
import OfferView from "@/shared/widgets/OfferView";
import { offerCreatorSelector } from "@/domains/offer/slice";
import getEstateImage from "@/domains/estate/helpers/getEstateImage";
import useOfferCreatorTenant from "@/domains/offer/hooks/useOfferCreatorTenant";
import OfferEstateInfo from "@/domains/offer/components/estate/OfferEstateInfo";
import OfferEstateLocation from "@/domains/offer/components/estate/OfferEstateLocation";

export default function OfferEstateTenantCreatorContainer({ isShow }: { isShow: boolean }) {
  const { t } = useTranslation("offer");

  const offerCreator = useAppSelector(offerCreatorSelector);

  const [isAgree, setIsAgree] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const { estates, create, close, pending, loading } = useOfferCreatorTenant(isShow);

  const currentEstate = estates[activeIndex];

  const level = estates.length > 0 ? t(`estate:estate.level.${currentEstate.level}`) : "";

  const complexId =
    currentEstate?.entity && currentEstate.entity.type === EstateType.Apartment
      ? currentEstate?.entity?.content?.complexId
      : null;

  const src =
    estates.length > 0
      ? getEstateImage(
          { id: currentEstate?.id, type: currentEstate?.entity?.type, complexId },
          "medium",
        )
      : "";

  const flatNumber =
    currentEstate?.entity && currentEstate.entity.type === EstateType.Apartment
      ? `, ${t("estate:estate.flatNum")}.${currentEstate.entity.content.room}`
      : "";

  const address =
    estates.length > 0
      ? `${t("offer:offer.saintPetersburg")}, ${currentEstate?.address?.streetName} ${currentEstate?.address?.index}${flatNumber}`
      : "";

  const onSubmit = useCallback(() => {
    return create(currentEstate?.id, offerCreator?.targetCharacter?.id);
  }, [create, currentEstate?.id, offerCreator?.targetCharacter?.id]);

  const toggleAgree = useCallback(() => {
    setIsAgree((state) => !state);
  }, []);

  useEffect(() => {
    if (!isShow) {
      setIsAgree(false);
      setActiveIndex(0);
    }
  }, [isShow]);

  const notEmpty = !pending && !loading && estates?.length > 0;

  const footer = (
    <Button
      className={clsx("h-11 mr-1", !notEmpty && "!opacity-0 pointer-events-none")}
      onClick={onSubmit}
      disabled={!isAgree || pending}
      loading={pending}
    >
      {t("offer.sendInvite")}
    </Button>
  );

  const leftText = (
    <div className="flex items-center gap-2.5">
      <House3Icon size={17} />
      <p className="text-15 font-bold leading-none">
        {t("offer.tenant.info.house")} #{currentEstate?.id}
      </p>
    </div>
  );

  const rightText = (
    <div className="flex flex-col gap-0.5 items-end ml-auto">
      <p className="text-14 font-bold">{formatPrice(currentEstate?.baseFee)} ₽</p>
      <p className="text-11 font-medium uppercase">{t("offer.estate.info.baseFee")}</p>
    </div>
  );

  return (
    <Modal
      open={isShow}
      title={t("offer.tenant.creator.title")}
      onClose={close}
      footer={footer}
      className="w-[30rem] pt-6.5 pb-7.5 pl-7.5 pr-6.5"
      classNameCloseBtn="top-6.5 right-7.5"
      style={{ fontFamily: "TT Norms Pro" }}
    >
      <div className="relative flex flex-col min-h-[31.375rem]">
        {!notEmpty || pending || loading ? (
          <div className="absolute top-0 left-0 z-3 flex justify-center items-center size-full text-16 font-bold text-white/50 text-center leading-none uppercase">
            {!pending && !loading && t("offer.empty")}
            {(pending || loading) && <Loader />}
          </div>
        ) : (
          <>
            {offerCreator && offerCreator.targetCharacter && (
              <OfferPlayerInfo
                name={offerCreator.targetCharacter.name}
                avatar={offerCreator.targetCharacter.avatar}
                id={offerCreator.targetCharacter.id}
                text={t("offer.player")}
              />
            )}
            {estates && (
              <>
                <div>
                  <p className="mt-4 mb-3.5 text-16 font-medium">
                    {t("offer.tenant.creator.residence")}:
                  </p>
                  <OfferView
                    src={src}
                    level={level}
                    list={estates || []}
                    activeIndex={activeIndex}
                    setActiveIndex={setActiveIndex}
                    leftText={leftText}
                    rightText={rightText}
                  />
                </div>
                <OfferEstateLocation address={address} />
                <OfferEstateInfo
                  livingCount={currentEstate?.livingCount}
                  garageCount={currentEstate?.garageCount}
                  livingCapacity={currentEstate?.livingCapacity}
                  garageCapacity={currentEstate?.garageCapacity}
                />
              </>
            )}
            <div className="flex items-center gap-2.5 mt-5">
              <Checkbox checked={isAgree} onChange={toggleAgree} />
              <button
                type="button"
                onClick={toggleAgree}
                className="text-14 font-medium text-inverted"
              >
                {t("offer.tenant.creator.agree")}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
